import { action, makeObservable, observable, toJS } from "mobx";
import { RootStore } from "src/stores/RootStore";
import { IUnitData } from "./InvoiceTypes";
import { ErpType } from "src/network/User";
import {
    GetUnitDataByRealestateId,
    GetUnitDataByRealestateIdVariables
} from "src/api/generated/GetUnitDataByRealestateId";
import { GET_UNIT_DATA_BY_REALESTATEID } from "src/api/ticket";
import { apolloClientInstance } from "src/network/apolloClientInstance";

export class AccountingUnitSearchStore {
    rootStore: RootStore;

    unitData: IUnitData[] = [];

    // Request cache for GET_UNIT_DATA_BY_REALESTATEID keyed by realestate id. Rows with the same
    // realestate share one request (also while in flight). Not observable on purpose.
    private unitDataRequests: Map<string, Promise<IUnitData[]>> = new Map();

    // This is an array because every Accounting Row has its own unit value
    currentUnitSearchQuery: string[] = [];
    selectedUnitQueryString: string[] = [];
    selectedUnitName: string[] = [];

    triggerFocusNextAccountingExtraCostDateInput: { isActive: boolean; index: number } = { isActive: false, index: 0 };

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            unitData: observable,
            currentUnitSearchQuery: observable,
            selectedUnitQueryString: observable,
            selectedUnitName: observable,
            triggerFocusNextAccountingExtraCostDateInput: observable,

            reset: action,
            removeAccountingRow: action,
            setCurrentUnitSearchQuery: action,
            setSelectedUnitQueryString: action,
            setSelectedUnitName: action,
            setTriggerFocusNextAccountingExtraCostDateInput: action,
            filteredUnitData: action,
            loadUnitData: action,
            setUnitData: action
        });
    }

    loadUnitData = async (realestateId: string): Promise<IUnitData[]> => {
        const unitData = await this.fetchUnitData(realestateId);

        // The unit dropdown reads this list for the realestate of the row being edited, so it has
        // to be (re-)applied on every call - also when the data came from the cache.
        this.setUnitData(unitData);

        return unitData;
    };

    private fetchUnitData = (realestateId: string): Promise<IUnitData[]> => {
        const pendingRequest = this.unitDataRequests.get(realestateId);
        if (pendingRequest) {
            return pendingRequest;
        }

        const request = apolloClientInstance
            .query<GetUnitDataByRealestateId, GetUnitDataByRealestateIdVariables>({
                query: GET_UNIT_DATA_BY_REALESTATEID,
                variables: {
                    realestateid: realestateId
                }
            })
            .then(({ data }) => {
                const unitData: IUnitData[] = [];

                data.houses.forEach((house) => {
                    house.units.forEach((unit) => {
                        unitData.push({
                            houseId: house.id,
                            unitId: unit.id,
                            unitNumber: unit.number ?? undefined,
                            unitName: unit.name ?? ""
                        });
                    });
                });

                // Sorting units by name
                unitData.sort((unitA, unitB) => {
                    if (unitA.unitName < unitB.unitName) {
                        return -1;
                    }
                    if (unitA.unitName > unitB.unitName) {
                        return 1;
                    }
                    return 0;
                });

                return unitData;
            })
            .catch((error) => {
                // Do not cache failures - the next caller gets a fresh attempt.
                this.unitDataRequests.delete(realestateId);
                throw error;
            });

        this.unitDataRequests.set(realestateId, request);

        return request;
    };

    setUnitData = (unitData: IUnitData[]) => {
        this.unitData = unitData;
    };

    reset = () => {
        this.currentUnitSearchQuery = [];
        this.selectedUnitQueryString = [];
        this.selectedUnitName = [];
        this.unitDataRequests.clear();
    };

    removeAccountingRow = (accountingRowIndex: number) => {
        this.currentUnitSearchQuery.splice(accountingRowIndex, 1);
        this.selectedUnitQueryString.splice(accountingRowIndex, 1);
        this.selectedUnitName.splice(accountingRowIndex, 1);
    };

    // Getters
    getFormattedUnitFieldValue = (unit: IUnitData): string => {
        return `${unit.unitName} [${unit.unitNumber}]`;
    };

    getCurrentUnitSearchQuery(accountingRowIndex?: number) {
        if (accountingRowIndex !== undefined) {
            return this.currentUnitSearchQuery[accountingRowIndex];
        }
    }

    getSelectedUnitQueryString(accountingRowIndex?: number) {
        if (accountingRowIndex !== undefined) {
            return this.selectedUnitQueryString[accountingRowIndex];
        }
    }

    getSelectedUnitName(accountingRowIndex?: number) {
        if (accountingRowIndex !== undefined) {
            return this.selectedUnitName[accountingRowIndex];
        }
    }

    // Setters
    setCurrentUnitSearchQuery(currentUnitSearchQuery: string, accountingRowIndex?: number) {
        if (accountingRowIndex !== undefined) {
            this.currentUnitSearchQuery[accountingRowIndex] = currentUnitSearchQuery;
        }
    }

    setSelectedUnitQueryString(selectedUnitQueryString: string, accountingRowIndex?: number) {
        if (accountingRowIndex !== undefined) {
            this.selectedUnitQueryString[accountingRowIndex] = selectedUnitQueryString;
        }
    }

    setSelectedUnitName(selectedUnitName: string, accountingRowIndex?: number) {
        if (accountingRowIndex !== undefined) {
            this.selectedUnitName[accountingRowIndex] = selectedUnitName;
        }
    }

    /*  This function triggers the focus on the next Account field after selecting a Realestate
        For IT2 this will always be the Account field in the first accounting row
        For RIMO this will be the Account field in the current accounting row
    */
    setTriggerFocusNextAccountingExtraCostDateInput(
        triggerFocusNextAccountingExtraCostDateInput: boolean,
        accountingRowIndex?: number
    ) {
        const erpType = this.rootStore.authStore.user?.erpType;

        if (erpType === ErpType.RIMO && accountingRowIndex !== undefined) {
            this.triggerFocusNextAccountingExtraCostDateInput = {
                isActive: triggerFocusNextAccountingExtraCostDateInput,
                index: accountingRowIndex
            };
        }
    }

    filteredUnitData(accountingRowIndex?: number): IUnitData[] {
        const selectedRealestate =
            accountingRowIndex !== undefined &&
            this.rootStore.invoiceStore.currentInvoice?.accountings &&
            this.rootStore.invoiceStore.currentInvoice?.accountings[accountingRowIndex]
                ? this.rootStore.invoiceStore.currentInvoice?.accountings[accountingRowIndex].realestate
                : undefined;

        // console.log("filteredUnitData - accountingRowIndex:", accountingRowIndex);

        // console.log(
        //     "filteredUnitData - currentInvoice.accountings:",
        //     toJS(this.rootStore.invoiceStore.currentInvoice?.accountings[accountingRowIndex ?? 0].realestate)
        // );

        if (!selectedRealestate || accountingRowIndex === undefined) {
            return [];
        }

        const unitData = this.rootStore.unitSearchStore.unitData;

        const currentUnitSearchQuery = this.currentUnitSearchQuery[accountingRowIndex] ?? "";
        const selectedUnitQueryString = this.selectedUnitQueryString[accountingRowIndex] ?? "";

        if (currentUnitSearchQuery === selectedUnitQueryString || currentUnitSearchQuery === "") {
            return unitData;
        }

        const filteredUnitData = unitData.filter((unit) => {
            const unitName = this.getFormattedUnitFieldValue(unit);

            return unitName.toLowerCase().includes(currentUnitSearchQuery.toLowerCase());
        });

        return filteredUnitData;
    }
}
